import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Loader,
  FileText,
  AlertCircle,
  ArrowRight,
  Tag,
  FileCheck,
  Clock,
} from 'lucide-react';
import { personalizedSummaryService, PersonalizedSummaryRequest, PersonalizedSummary } from '../../lib/personalizedSummaryService';
import { supabase, STORAGE_BUCKET } from '../../lib/supabase';
import { useRequireAuth, consumePendingAuthIntent } from '../../hooks/useRequireAuth';
import { userTierService } from '../../lib/userTierService';
import { getFilingTypeTag, formatDateForDisplay } from '../../utils/docket-search';
import { GenerateSummaryForm } from './summaries/GenerateSummaryForm';
import { GenerationProgress } from './summaries/GenerationProgress';
import { type SelectedStakeholder } from './StakeholderSelector';
import type { PrevailingInstrument } from './ProceedingDetailHeader';
import { notifyCacheHit } from '../../lib/cacheAlertService';
import { fetchProceedingsFilings } from '../../lib/proceedingsFilingRetrieval';
import { batchCheckSpamFilers } from '../../lib/spamFilerService';
import {
  filterDefenseExpressComments,
  getProceedingFilingPeriod,
  isCommentOrReplyFiling,
} from '../../utils/proceedingsFilingsUi';

interface Filing {
  id_submission: string;
  filers?: Array<{ name: string }>;
  date_received?: string;
  date_submission?: string;
  submissiontype?: { short: string };
  filing_type?: string;
  express_comment?: number;
  proceedings?: Array<{ name: string }>;
  text_data?: string;
}

interface NPRMTimeline {
  id: string;
  docket_number: string;
  comments_period_start: string;
  comments_period_end: string | null;
  reply_comments_period_start: string | null;
  reply_comments_period_end: string | null;
}

interface PeriodSubmission {
  submission_id: string;
  period_type: 'comments' | 'reply_comments';
  date_submission?: string | null;
  nprm_timeline_id?: string;
}

interface PublicNoticeDocumentRef {
  filename?: string;
  src?: string;
  url?: string;
  file_url?: string;
  document_url?: string;
  link?: string;
  href?: string;
  mime_type?: string;
  type?: string;
  [k: string]: unknown;
}

interface TopPublicNoticeFiling {
  id_submission: string;
  date_received: string | null;
  filer_name: string | null;
  submissiontype_short: string | null;
  documents: PublicNoticeDocumentRef[];
}

interface SummariesSectionProps {
  docketNumber: string;
  commentDeadline: Date | null;
  replyCommentDeadline: Date | null;
  nprmCount: number | null;
  /** While docket metadata loads, keep NPRM UI from flashing hidden. */
  fetchingNprmCount?: boolean;
  prevailingInstrument?: PrevailingInstrument;
  /** Same synthetic PN row as FilingsSection so summaries bucket by comment windows. */
  syntheticTimelineForFilings?: NPRMTimeline | null;
  /** Count of Public Notice filings on the docket (from `get-docket-data`). */
  publicNoticeCount?: number | null;
  /** Most-recent-first Public Notice filing; enables on-demand PN upload. */
  topPublicNoticeDocument?: TopPublicNoticeFiling | null;
}

interface PeriodSummaryState {
  isGenerating: boolean;
  /** 0–100 from the backend job row; drives the loading screen stage. */
  generationProgress: number;
  /** Epoch ms when the user kicked off generation; drives the elapsed-time
   *  counter on the loading screen. Reset each time a new job starts. */
  generationStartedAt: number | null;
  jobId: string | null;
  summary: PersonalizedSummary | null;
  error: string | null;
  isCreatingNewSummary: boolean;
  topics: string[];
  selectedNprmFile: string | null;
  selectedPnFile: string | null;
  topicsInputMode: 'form' | 'paragraph';
  selectionMode: 'all' | 'specific';
  selectedFilingIds: Set<string>;
  /** Submission IDs of out-of-window filings the user has opted into.
   *  Distinct from selectedFilingIds so we can render two separate
   *  pickers (in-window and out-of-window). Merged at submit time. */
  selectedOutOfWindowIds: Set<string>;
  /** Submission IDs of non-comment-family filings (NPRMs, Notices, etc.)
   *  the user has opted in as mis-classified comments. Merged into the
   *  request at submit time; the backend expands the storage-folder
   *  search to cover their filing types. */
  selectedMisclassifiedIds: Set<string>;
  forceRefresh: boolean;
  detailLevel: 'concise' | 'standard' | 'detailed';
  selectedStakeholders: SelectedStakeholder[];
  /** Optional user-supplied steering text for the executive summary.
   *  Threaded into the detailed-exec prompt server-side so the model
   *  knows which threads the attorney wants to emphasize. */
  executiveSummaryFocus: string;
}

export const SummariesSection: React.FC<SummariesSectionProps> = ({
  docketNumber,
  commentDeadline: _commentDeadline,
  replyCommentDeadline: _replyCommentDeadline,
  nprmCount,
  fetchingNprmCount = false,
  prevailingInstrument = 'neither',
  syntheticTimelineForFilings = null,
  publicNoticeCount = null,
  topPublicNoticeDocument = null,
}) => {
  const requireAuth = useRequireAuth();
  const [filings, setFilings] = useState<Filing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nprmTimelines, setNprmTimelines] = useState<NPRMTimeline[]>([]);
  const [periodSubmissions, setPeriodSubmissions] = useState<PeriodSubmission[]>([]);
  const [periodSubmissionsMap, setPeriodSubmissionsMap] = useState<Map<string, PeriodSubmission>>(new Map());
  const [timelineResolvedForDocket, setTimelineResolvedForDocket] = useState<string | null>(null);
  const [nprmFiles, setNprmFiles] = useState<Array<{name: string, path: string, created_at: string}>>([]);
  const [isLoadingNprmFiles, setIsLoadingNprmFiles] = useState(false);
  const [isFetchingNprmFiles, setIsFetchingNprmFiles] = useState(false);
  const [fetchingNprmError, setFetchingNprmError] = useState<string | null>(null);
  const [pnFiles, setPnFiles] = useState<Array<{name: string, path: string, created_at: string}>>([]);
  const [isLoadingPnFiles, setIsLoadingPnFiles] = useState(false);
  const [isFetchingPnFiles, setIsFetchingPnFiles] = useState(false);
  const [fetchingPnError, setFetchingPnError] = useState<string | null>(null);
  const [activePeriodTab, setActivePeriodTab] = useState<'comments' | 'reply_comments'>('comments');
  const [filingsHydratedForDocket, setFilingsHydratedForDocket] = useState<string | null>(null);
  const topicsStepRef = useRef<HTMLDivElement | null>(null);
  const topicsFirstFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const summariesActiveTimeline = useMemo(
    () => syntheticTimelineForFilings ?? nprmTimelines[0] ?? null,
    [syntheticTimelineForFilings, nprmTimelines]
  );

  const showPnPicker = useMemo(
    () =>
      prevailingInstrument === 'public_notice' &&
      (
        (publicNoticeCount ?? 0) > 0 ||
        !!topPublicNoticeDocument ||
        pnFiles.length > 0 ||
        isLoadingPnFiles ||
        isFetchingPnFiles
      ),
    [
      prevailingInstrument,
      publicNoticeCount,
      topPublicNoticeDocument,
      pnFiles.length,
      isLoadingPnFiles,
      isFetchingPnFiles,
    ]
  );

  const showNprmPicker = useMemo(
    () =>
      prevailingInstrument !== 'public_notice' &&
      (
        fetchingNprmCount ||
        (nprmCount ?? 0) > 0 ||
        nprmFiles.length > 0 ||
        isLoadingNprmFiles ||
        isFetchingNprmFiles
      ),
    [
      prevailingInstrument,
      fetchingNprmCount,
      nprmCount,
      nprmFiles.length,
      isLoadingNprmFiles,
      isFetchingNprmFiles,
    ]
  );

  // Separate state for each period
  const [commentPeriodState, setCommentPeriodState] = useState<PeriodSummaryState>({
    isGenerating: false,
    generationProgress: 0,
    generationStartedAt: null,
    jobId: null,
    summary: null,
    error: null,
    isCreatingNewSummary: true,
    topics: [''],
    selectedNprmFile: null,
    selectedPnFile: null,
    topicsInputMode: 'form',
    selectionMode: 'all',
    selectedFilingIds: new Set(),
    selectedOutOfWindowIds: new Set(),
    selectedMisclassifiedIds: new Set(),
    forceRefresh: false,
    detailLevel: 'concise',
    executiveSummaryFocus: '',
    selectedStakeholders: [],
    executiveSummaryFocus: '',
  });

  const [replyCommentPeriodState, setReplyCommentPeriodState] = useState<PeriodSummaryState>({
    isGenerating: false,
    generationProgress: 0,
    generationStartedAt: null,
    jobId: null,
    summary: null,
    error: null,
    isCreatingNewSummary: true,
    topics: [''],
    selectedNprmFile: null,
    selectedPnFile: null,
    topicsInputMode: 'form',
    selectionMode: 'all',
    selectedFilingIds: new Set(),
    selectedOutOfWindowIds: new Set(),
    selectedMisclassifiedIds: new Set(),
    forceRefresh: false,
    detailLevel: 'concise',
    executiveSummaryFocus: '',
    selectedStakeholders: [],
    executiveSummaryFocus: '',
  });

  useEffect(() => {
    const singleFileName = nprmFiles.length === 1 ? nprmFiles[0].name : null;
    const validNames = new Set(nprmFiles.map(file => file.name));

    const reconcileNprmSelection = (state: PeriodSummaryState): PeriodSummaryState => {
      if (singleFileName) {
        if (state.selectedNprmFile === singleFileName) return state;
        return { ...state, selectedNprmFile: singleFileName };
      }

      if (state.selectedNprmFile && !validNames.has(state.selectedNprmFile)) {
        return { ...state, selectedNprmFile: null };
      }

      // Quick-summary default: with multiple NPRM revisions and none chosen,
      // default to the first (most recent) so the user isn't blocked by a
      // "Select an NPRM document" gate. They can still switch it in the picker.
      if (!state.selectedNprmFile && nprmFiles.length > 1) {
        return { ...state, selectedNprmFile: nprmFiles[0].name };
      }

      return state;
    };

    setCommentPeriodState(prev => reconcileNprmSelection(prev));
    setReplyCommentPeriodState(prev => reconcileNprmSelection(prev));
  }, [nprmFiles]);

  useEffect(() => {
    const singleFileName = pnFiles.length === 1 ? pnFiles[0].name : null;
    const validNames = new Set(pnFiles.map(file => file.name));

    const reconcilePnSelection = (state: PeriodSummaryState): PeriodSummaryState => {
      if (singleFileName) {
        if (state.selectedPnFile === singleFileName) return state;
        return { ...state, selectedPnFile: singleFileName };
      }

      if (state.selectedPnFile && !validNames.has(state.selectedPnFile)) {
        return { ...state, selectedPnFile: null };
      }

      // Quick-summary default: multiple PN documents, none chosen -> default to
      // the first so the user isn't blocked (they can switch it in the picker).
      if (!state.selectedPnFile && pnFiles.length > 1) {
        return { ...state, selectedPnFile: pnFiles[0].name };
      }

      return state;
    };

    setCommentPeriodState(prev => reconcilePnSelection(prev));
    setReplyCommentPeriodState(prev => reconcilePnSelection(prev));
  }, [pnFiles]);

  const PROCEEDINGS_FILING_LIMIT = 200;

  // Fetch filings using the same ECFS + post-processing path as FilingsSection
  // (broad fallback default on, express defense filter, spam excluded, COMMENT/REPLY only).
  const fetchAllFilings = useCallback(async (
    timeline: NPRMTimeline | null = null,
    options: { allowBroadFallback?: boolean } = {}
  ) => {
    if (!docketNumber) return;

    try {
      setLoading(true);
      setError(null);
      const retrieval = await fetchProceedingsFilings(
        docketNumber,
        timeline || undefined,
        PROCEEDINGS_FILING_LIMIT,
        { allowBroadFallback: options.allowBroadFallback ?? true }
      );

      let working = filterDefenseExpressComments(
        retrieval.filings as Array<Filing & { express_comment?: unknown }>
      ) as Filing[];

      // The DB path returns already-de-spammed rows (server-side filter via
      // is_spam column populated by ingest trigger). Only run the
      // edge-function spam check on ECFS-fallback retrievals.
      if (retrieval.meta.sourceMode !== 'db') {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;
        const filerNames = working.map((f) => f.filers?.[0]?.name || 'Unknown_Filer');
        const { spamNames } = await batchCheckSpamFilers(filerNames, user?.id);
        working = working.filter((filing) => {
          const filerName = filing.filers?.[0]?.name || 'Unknown_Filer';
          return !spamNames.has(filerName);
        });
      }

      // Keep COMMENT / REPLY_COMM as the primary set. We used to drop
      // every other filing type here, but doing so silently hid late
      // filings and supplemental docs from the attorney. They're now
      // surfaced via the out-of-window opt-in inside the Scope section
      // of the generate form. Filings whose date falls outside both
      // windows naturally land in filingsByPeriod.others.
      const isCommentOrReply = (f: Filing): boolean => isCommentOrReplyFiling(f);
      const primary = working.filter(isCommentOrReply);
      const supplemental = working.filter((f) => !isCommentOrReply(f));
      working = [...primary, ...supplemental];

      setFilings(working);
      setFilingsHydratedForDocket(docketNumber);
      if (import.meta.env.DEV) {
        console.log('[SummariesRetrieval]', {
          docketNumber,
          sourceMode: retrieval.meta.sourceMode,
          fallbackUsed: retrieval.meta.fallbackUsed,
          fallbackReason: retrieval.meta.fallbackReason,
          params: retrieval.meta.params,
          rawFilingCount: retrieval.filings.length,
          filingsAfterFilingsTabRules: working.length,
        });
      }
    } catch (err) {
      console.error('Error fetching filings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch filings');
    } finally {
      setLoading(false);
    }
  }, [docketNumber]);

  // Fetch timeline data
  const fetchTimelineData = useCallback(async () => {
    if (!docketNumber) return;

    let resolvedTimeline: NPRMTimeline | null = null;

    try {
      setTimelineResolvedForDocket(null);
      // Fetch NPRM timelines
      const { data: timelines, error: timelineError } = await supabase
        .from('nprm_timelines')
        .select('*')
        .eq('docket_number', docketNumber)
        .order('fcc_release_date', { ascending: false });

      if (timelineError) {
        console.error('Error fetching timelines:', timelineError);
      } else {
        setNprmTimelines(timelines || []);
        resolvedTimeline = timelines && timelines.length > 0 ? timelines[0] : null;
      }

      // Fetch period_submissions
      const { data: periodData, error: periodError } = await supabase
        .from('period_submissions')
        .select('submission_id, period_type, date_submission, nprm_timeline_id')
        .eq('docket_number', docketNumber);

      if (periodError) {
        console.warn('Error fetching period_submissions:', periodError.message);
        setPeriodSubmissions([]);
        setPeriodSubmissionsMap(new Map());
      } else {
        const periodList = periodData || [];
        setPeriodSubmissions(periodList);
        const map = new Map<string, PeriodSubmission>();
        periodList.forEach((ps: PeriodSubmission) => {
          map.set(ps.submission_id, ps);
        });
        setPeriodSubmissionsMap(map);
      }
    } catch (err) {
      console.error('Error fetching timeline data:', err);
    } finally {
      setTimelineResolvedForDocket(docketNumber);
    }

    // Call fetchAllFilings immediately with the locally-resolved timeline rather
    // than waiting for summariesActiveTimeline state to propagate through the
    // effect chain. React 18 batching can cause summariesActiveTimeline to still
    // be null when the old effect-at-847 fires even though nprmTimelines was just set.
    fetchAllFilings(resolvedTimeline ?? syntheticTimelineForFilings ?? null);
  }, [docketNumber, fetchAllFilings, syntheticTimelineForFilings]);

  // Load NPRM files from storage
  const loadNprmFiles = useCallback(async () => {
    if (!docketNumber) return;

    try {
      setIsLoadingNprmFiles(true);
      setFetchingNprmError(null);

      // Check storage for NPRM files
      const { data: files, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(`ecfs/${docketNumber}/NPRM`, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        console.warn('Error listing NPRM files:', listError);
      }

      const nprmFileItems: Array<{name: string, path: string, created_at: string}> = [];
      
      if (files && files.length > 0) {
        for (const file of files) {
          if (file.name && file.id) {
            nprmFileItems.push({
              name: file.name,
              path: `ecfs/${docketNumber}/NPRM/${file.name}`,
              created_at: file.created_at || new Date().toISOString()
            });
          }
        }
      }

      setNprmFiles(nprmFileItems);

      // If no files found and we have NPRM count, try fetching from ECFS
      if (nprmFileItems.length === 0 && nprmCount !== null && nprmCount > 0) {
        await fetchAndUploadNprmFiles();
      }
    } catch (err) {
      console.error('Failed to load NPRM files:', err);
      setFetchingNprmError(err instanceof Error ? err.message : 'Failed to load NPRM files');
    } finally {
      setIsLoadingNprmFiles(false);
    }
  }, [docketNumber, nprmCount]);

  // Fetch NPRM files from ECFS and upload to Supabase
  const fetchAndUploadNprmFiles = useCallback(async () => {
    if (!docketNumber || isFetchingNprmFiles) return;

    try {
      setIsFetchingNprmFiles(true);
      setFetchingNprmError(null);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-nprm-filings`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ docketNumber }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch NPRM files: ${response.status}`);
      }

      const data = await response.json();
      notifyCacheHit(data, `NPRM files for ${docketNumber}`);
      if (data.filing && data.filing.length > 0) {
        // Files should be uploaded by the function, reload
        await loadNprmFiles();
      }
    } catch (err) {
      console.error('Error fetching NPRM files:', err);
      setFetchingNprmError(err instanceof Error ? err.message : 'Failed to fetch NPRM files');
    } finally {
      setIsFetchingNprmFiles(false);
    }
  }, [docketNumber, isFetchingNprmFiles, loadNprmFiles]);

  /**
   * Pick the primary PDF from a PN filing's `documents`. Mirrors the selection
   * used by `ProceedingDetailsPage.getPublicNoticeDocumentUrl`.
   */
  const pickPrimaryPnDocument = useCallback(
    (documents: PublicNoticeDocumentRef[] | undefined): PublicNoticeDocumentRef | null => {
      if (!documents || documents.length === 0) return null;
      const pdfDoc = documents.find((doc) => {
        const filename = (doc?.filename || '').toString().toLowerCase();
        const mime = (doc?.mime_type || doc?.type || '').toString().toLowerCase();
        const src = (doc?.src || doc?.url || doc?.file_url || doc?.document_url || '').toString().toLowerCase();
        if (filename.endsWith('.pdf')) return true;
        if (mime.includes('pdf')) return true;
        if (src.endsWith('.pdf')) return true;
        return false;
      });
      return pdfDoc || documents[0] || null;
    },
    []
  );

  // Fetch top Public Notice PDF from ECFS and upload to Supabase storage under
  // the hyphenated `ecfs/{docket}/PUBLIC-NOTICE/` path that the backend expects.
  const fetchAndUploadPnFile = useCallback(async () => {
    if (!docketNumber || isFetchingPnFiles) return;
    if (!topPublicNoticeDocument) return;

    const primary = pickPrimaryPnDocument(topPublicNoticeDocument.documents);
    const srcUrl =
      (primary?.src as string | undefined) ||
      (primary?.url as string | undefined) ||
      (primary?.file_url as string | undefined) ||
      (primary?.document_url as string | undefined) ||
      (primary?.link as string | undefined) ||
      (primary?.href as string | undefined) ||
      null;
    const fileName = primary?.filename;
    if (!primary || !srcUrl || !fileName) return;

    try {
      setIsFetchingPnFiles(true);
      setFetchingPnError(null);

      const uploadApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-ecfs-files`;
      const response = await fetch(uploadApiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          src: srcUrl,
          docketNumber,
          filerName: topPublicNoticeDocument.filer_name || 'FCC',
          fileName,
          submissionId: topPublicNoticeDocument.id_submission,
          // Keep the hyphenated name — upload-ecfs-files preserves it and the
          // external API expects ecfs/{docket}/PUBLIC-NOTICE/.
          filingType: 'PUBLIC-NOTICE',
          expressComment: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to upload Public Notice: ${response.status}`);
      }

      // Reload PN file list from storage now that the upload is done.
      await loadPnFiles();
    } catch (err) {
      console.error('Error fetching/uploading Public Notice file:', err);
      setFetchingPnError(err instanceof Error ? err.message : 'Failed to upload Public Notice file');
    } finally {
      setIsFetchingPnFiles(false);
    }
    // loadPnFiles is declared below; listing it here creates a circular dep warning
    // that we resolve by depending on the minimum viable inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docketNumber, isFetchingPnFiles, topPublicNoticeDocument, pickPrimaryPnDocument]);

  // List Public Notice files in `ecfs/{docket}/PUBLIC-NOTICE/`. If none present
  // and we have a top PN doc prop, trigger on-demand upload.
  const loadPnFiles = useCallback(async () => {
    if (!docketNumber) return;

    try {
      setIsLoadingPnFiles(true);
      setFetchingPnError(null);

      const { data: files, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(`ecfs/${docketNumber}/PUBLIC-NOTICE`, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        console.warn('Error listing Public Notice files:', listError);
      }

      const pnFileItems: Array<{name: string, path: string, created_at: string}> = [];
      if (files && files.length > 0) {
        for (const file of files) {
          if (file.name && file.id) {
            pnFileItems.push({
              name: file.name,
              path: `ecfs/${docketNumber}/PUBLIC-NOTICE/${file.name}`,
              created_at: file.created_at || new Date().toISOString(),
            });
          }
        }
      }

      setPnFiles(pnFileItems);

      if (pnFileItems.length === 0 && topPublicNoticeDocument) {
        await fetchAndUploadPnFile();
      }
    } catch (err) {
      console.error('Failed to load Public Notice files:', err);
      setFetchingPnError(err instanceof Error ? err.message : 'Failed to load Public Notice files');
    } finally {
      setIsLoadingPnFiles(false);
    }
  }, [docketNumber, topPublicNoticeDocument, fetchAndUploadPnFile]);

  const getFilingPeriod = useCallback(
    (filing: Filing): 'comments' | 'reply_comments' | 'other' =>
      getProceedingFilingPeriod(filing, {
        periodSubmissionsMap,
        activeTimeline: summariesActiveTimeline,
      }),
    [periodSubmissionsMap, summariesActiveTimeline]
  );

  // Filter filings by period. Comment-family submissions (COMMENT,
  // REPLY, REPLY COMM, EXPRESS-COMMENT) get bucketed by date into
  // 'comments', 'reply_comments', or 'others' (out-of-window opt-in).
  // Everything else — NPRMs, Notices, Letters, Public Drafts — goes
  // into 'misclassified', regardless of date, since ECFS sometimes
  // mis-tags a comment as one of those types.
  const filingsByPeriod = useMemo(() => {
    const comments: Filing[] = [];
    const replyComments: Filing[] = [];
    const others: Filing[] = [];
    const misclassified: Filing[] = [];

    const isCommentLike = (filing: Filing) => {
      const t = (filing.submissiontype?.short || filing.filing_type || '')
        .toString()
        .trim()
        .toUpperCase();
      return (
        t === 'COMMENT' ||
        t === 'REPLY' ||
        t === 'REPLY COMM' ||
        t === 'REPLY_COMM' ||
        t === 'REPLY TO COMMENTS' ||
        t === 'EXPRESS-COMMENT' ||
        t === 'EXPRESS COMMENT'
      );
    };

    filings.forEach(filing => {
      if (!isCommentLike(filing)) {
        misclassified.push(filing);
        return;
      }
      const period = getFilingPeriod(filing);
      if (period === 'comments') {
        comments.push(filing);
      } else if (period === 'reply_comments') {
        replyComments.push(filing);
      } else {
        others.push(filing);
      }
    });

    return { comments, replyComments, others, misclassified };
  }, [filings, getFilingPeriod]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const replyIds = filingsByPeriod.replyComments.map(filing => filing.id_submission);
    console.group(`[SummariesDebug] ${docketNumber} reply-period source IDs`);
    console.log('Reply IDs used in Comment Summaries:', replyIds);
    console.log('Reply count in Comment Summaries:', replyIds.length);
    console.groupEnd();
  }, [filingsByPeriod.replyComments, docketNumber]);

  // Get available tabs (only those with filings)
  const availableTabs = useMemo(() => {
    const tabs = [
      { id: 'comments' as const, label: 'Comment Period', count: filingsByPeriod.comments.length },
      { id: 'reply_comments' as const, label: 'Reply Comment Period', count: filingsByPeriod.replyComments.length }
    ];
    return tabs.filter(tab => tab.count > 0);
  }, [filingsByPeriod]);

  // Initialize active tab based on available filings
  useEffect(() => {
    if (availableTabs.length > 0) {
      const currentTab = availableTabs.find(tab => tab.id === activePeriodTab);
      if (!currentTab || currentTab.count === 0) {
        // Switch to first available tab
        setActivePeriodTab(availableTabs[0].id);
      }
    }
  }, [availableTabs, activePeriodTab]);

  // Load existing summaries for each period
  const loadExistingSummaries = useCallback(async () => {
    if (!docketNumber) return;

    try {
      const summaries = await personalizedSummaryService.getUserPersonalizedSummaries(docketNumber);
      
      // Filter summaries by period_type
      const commentSummaries = summaries.filter(s => s.period_type === 'comments' || (!s.period_type && (!s.filing_type || s.filing_type !== 'REPLY_COMM')));
      const replyCommentSummaries = summaries.filter(s => s.period_type === 'reply_comments' || (!s.period_type && s.filing_type === 'REPLY_COMM'));
      
      // Show most recent summary for comment period (if any)
      if (commentSummaries.length > 0) {
        const commentSummary = commentSummaries[0];
        setCommentPeriodState(prev => {
          if (prev.summary?.id === commentSummary.id) return prev;
          return { ...prev, summary: commentSummary, isCreatingNewSummary: false };
        });
      }
      
      // Show most recent summary for reply comment period (if any)
      if (replyCommentSummaries.length > 0) {
        const replySummary = replyCommentSummaries[0];
        setReplyCommentPeriodState(prev => {
          if (prev.summary?.id === replySummary.id) return prev;
          return { ...prev, summary: replySummary, isCreatingNewSummary: false };
        });
      }
    } catch (err) {
      console.error('Error loading existing summaries:', err);
    }
  }, [docketNumber]);

  // Generate summary for a period
  const generatePeriodSummary = useCallback(async (
    period: 'comments' | 'reply_comments',
    state: PeriodSummaryState
  ) => {
    console.log('🚀 generatePeriodSummary called', { period, docketNumber, state });
    if (!docketNumber) {
      console.log('❌ No docket number, returning early');
      return;
    }

    // Flip the spinner ON immediately so the click feels responsive. The
    // tier-check + validations below can take a second on cold caches; if
    // we wait until after them, the user clicks and sees nothing happen.
    // Any early-return below clears it again so the button doesn't get
    // stuck in a generating state on validation failure.
    const startedAt = Date.now();
    if (period === 'comments') {
      setCommentPeriodState(prev => ({
        ...prev,
        isGenerating: true,
        generationProgress: 0,
        generationStartedAt: startedAt,
        error: null,
      }));
    } else {
      setReplyCommentPeriodState(prev => ({
        ...prev,
        isGenerating: true,
        generationProgress: 0,
        generationStartedAt: startedAt,
        error: null,
      }));
    }
    const clearGenerating = () => {
      if (period === 'comments') {
        setCommentPeriodState(prev => ({
          ...prev,
          isGenerating: false,
          generationProgress: 0,
          generationStartedAt: null,
        }));
      } else {
        setReplyCommentPeriodState(prev => ({
          ...prev,
          isGenerating: false,
          generationProgress: 0,
          generationStartedAt: null,
        }));
      }
    };

    // Check user tier
    const canGenerateSummary = await userTierService.canPerformAction('summary');
    if (!canGenerateSummary) {
      const errorMsg = 'You have reached your summary generation limit for this month. Please upgrade your plan to continue generating summaries.';
      if (period === 'comments') {
        setCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      } else {
        setReplyCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      }
      clearGenerating();
      return;
    }

    // Validate topics
    const filteredTopics = state.topics.filter(t => t.trim());
    if (filteredTopics.length === 0) {
      const errorMsg = 'Please enter at least one topic';
      if (period === 'comments') {
        setCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      } else {
        setReplyCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      }
      clearGenerating();
      return;
    }

    // Get filings for this period
    const periodFilings = period === 'comments' ? filingsByPeriod.comments : filingsByPeriod.replyComments;
    if (periodFilings.length === 0) {
      const errorMsg = `No filings found in ${period === 'comments' ? 'Comment' : 'Reply Comment'} Period`;
      if (period === 'comments') {
        setCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      } else {
        setReplyCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      }
      clearGenerating();
      return;
    }

    // Validate selection when in specific mode. Out-of-window opt-ins
    // also count — user can summarize ONLY out-of-window filings if they
    // want to.
    if (
      state.selectionMode === 'specific' &&
      state.selectedFilingIds.size === 0 &&
      state.selectedOutOfWindowIds.size === 0
    ) {
      const errorMsg = 'Please select at least one filing to summarize';
      if (period === 'comments') {
        setCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      } else {
        setReplyCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      }
      clearGenerating();
      return;
    }

    if (prevailingInstrument === 'public_notice') {
      if (pnFiles.length > 1 && !state.selectedPnFile) {
        const errorMsg = 'Please select a Public Notice document before generating the summary';
        if (period === 'comments') {
          setCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
        } else {
          setReplyCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
        }
        clearGenerating();
        return;
      }
    } else if (nprmFiles.length > 1 && !state.selectedNprmFile) {
      const errorMsg = 'Please select an NPRM document before generating the summary';
      if (period === 'comments') {
        setCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      } else {
        setReplyCommentPeriodState(prev => ({ ...prev, error: errorMsg }));
      }
      clearGenerating();
      return;
    }

    // Get selected comment IDs based on selection mode + merge any
    // out-of-window filings the attorney opted into + any
    // mis-classified filings (NPRMs/Notices/etc.) the attorney has
    // identified as actually being comments.
    const inWindowIds = state.selectionMode === 'all'
      ? periodFilings.map(filing => filing.id_submission)
      : Array.from(state.selectedFilingIds);
    const outOfWindowIds = Array.from(state.selectedOutOfWindowIds);
    const misclassifiedIds = Array.from(state.selectedMisclassifiedIds);
    // Dedup just in case (a submission shouldn't appear in multiple
    // buckets, but belt-and-suspenders).
    const selectedCommentIds = Array.from(
      new Set([...inWindowIds, ...outOfWindowIds, ...misclassifiedIds])
    );

    // When the user opted in mis-classified filings, the backend needs
    // to look beyond the default COMMENT/EXPRESS-COMMENT storage
    // folders. Build the extra filing types from the actual filings
    // (e.g. "NPRM", "NOTICE") so retrieve_documents_from_supabase
    // picks them up.
    const misclassifiedFilingTypes = misclassifiedIds.length
      ? Array.from(
          new Set(
            filings
              .filter((f) => state.selectedMisclassifiedIds.has(f.id_submission))
              .map((f) => (f.submissiontype?.short || f.filing_type || '').toString().trim())
              .filter((t) => t.length > 0)
          )
        )
      : [];

    // Log submission IDs for Reply Comment Period
    if (period === 'reply_comments') {
      console.log('📋 Reply Comment Period - Submission IDs:', selectedCommentIds);
      console.log('📋 Reply Comment Period - Filing details:', periodFilings.map(f => ({
        id_submission: f.id_submission,
        filer: f.filers?.[0]?.name || 'Unknown',
        date: f.date_received || f.date_submission,
        filing_type: f.filing_type || f.submissiontype?.short
      })));
    }

    try {
      // isGenerating + error reset already happened at the top of this
      // handler so the spinner shows immediately on click.

      const isPnLed = prevailingInstrument === 'public_notice';

      // Flatten stakeholder selection into ids and names for the API.
      const relevantStakeholderIds = state.selectedStakeholders.flatMap(
        (s) => s.submission_ids
      );
      const relevantStakeholderNames = state.selectedStakeholders.map(
        (s) => s.filer_name
      );

      const trimmedFocus = state.executiveSummaryFocus.trim();
      // Default storage-folder lookup: COMMENT + EXPRESS-COMMENT (or
      // REPLY_COMM for reply-comments). Append any extra types from
      // mis-classified opt-ins so the backend can find those docs.
      const baseFilingTypes =
        period === 'reply_comments' ? ['REPLY_COMM'] : ['COMMENT', 'EXPRESS-COMMENT'];
      const filingTypes = Array.from(
        new Set([...baseFilingTypes, ...misclassifiedFilingTypes])
      );
      const request: PersonalizedSummaryRequest = {
        docketNumber,
        topics: filteredTopics,
        summaryType: 'multi-topic',
        maxDocsPerTopic: 100,
        detailLevel: state.detailLevel,
        selectedComments: selectedCommentIds,
        // Mutually exclusive: send either nprm_filename or public_notice_filename
        // based on the docket's prevailing instrument.
        nprm_filename: isPnLed ? undefined : (state.selectedNprmFile || undefined),
        public_notice_filename: isPnLed ? (state.selectedPnFile || undefined) : undefined,
        forceRefresh: state.forceRefresh,
        filingType: period === 'reply_comments' ? 'REPLY_COMM' : undefined,
        filingTypes,
        periodType: period,
        ...(trimmedFocus && { execFocus: trimmedFocus }),
        ...(relevantStakeholderIds.length > 0 && {
          relevant_stakeholder_ids: relevantStakeholderIds,
          relevant_stakeholder_names: relevantStakeholderNames,
        }),
      };

      console.log('📤 Calling generatePersonalizedSummary with request:', request);
      const response = await personalizedSummaryService.generatePersonalizedSummary(request);
      console.log('✅ generatePersonalizedSummary response:', response);

      // Update state with job ID. Keep isGenerating=true so the spinner
      // stays on until polling resolves; we only flip it off when the job
      // either completes successfully or fails.
      if (period === 'comments') {
        setCommentPeriodState(prev => ({
          ...prev,
          jobId: response.jobId,
          error: null,
        }));
      } else {
        setReplyCommentPeriodState(prev => ({
          ...prev,
          jobId: response.jobId,
          error: null,
        }));
      }

      // If the service returned the special 'cached' marker, no polling
      // needed — the summary already exists. Just reload the list.
      if (response.jobId === 'cached') {
        if (period === 'comments') {
          setCommentPeriodState(prev => ({
            ...prev,
            isGenerating: false,
            generationProgress: 0,
            generationStartedAt: null,
          }));
        } else {
          setReplyCommentPeriodState(prev => ({
            ...prev,
            isGenerating: false,
            generationProgress: 0,
            generationStartedAt: null,
          }));
        }
        loadExistingSummaries();
      } else {
        // Poll the job until it completes or fails. The previous code did a
        // one-shot reload after 2 s and never surfaced backend failures
        // (e.g. 403 plan-limit), leaving the user to wait forever for an
        // email that never came.
        (async () => {
          try {
            await personalizedSummaryService.pollJobStatus(
              response.jobId,
              (progressVal) => {
                // Drives the loading-screen progress bar + stage label.
                if (period === 'comments') {
                  setCommentPeriodState(prev => ({ ...prev, generationProgress: progressVal }));
                } else {
                  setReplyCommentPeriodState(prev => ({ ...prev, generationProgress: progressVal }));
                }
              }
            );
            // Job completed — clear spinner, refresh summaries.
            if (period === 'comments') {
              setCommentPeriodState(prev => ({
                ...prev,
                isGenerating: false,
                generationProgress: 100,
                generationStartedAt: null,
              }));
            } else {
              setReplyCommentPeriodState(prev => ({
                ...prev,
                isGenerating: false,
                generationProgress: 100,
                generationStartedAt: null,
              }));
            }
            loadExistingSummaries();
          } catch (jobErr) {
            // Job failed — surface the backend's error_message verbatim so
            // the user knows whether it was a plan limit, a transient error,
            // or something else.
            const msg = jobErr instanceof Error ? jobErr.message : 'Summary generation failed';
            console.error(`[summary-poll] ${period} job failed:`, msg);
            if (period === 'comments') {
              setCommentPeriodState(prev => ({
                ...prev,
                isGenerating: false,
                generationProgress: 0,
                generationStartedAt: null,
                jobId: null,
                error: msg,
              }));
            } else {
              setReplyCommentPeriodState(prev => ({
                ...prev,
                isGenerating: false,
                generationProgress: 0,
                generationStartedAt: null,
                jobId: null,
                error: msg,
              }));
            }
          }
        })();
      }

    } catch (err) {
      console.error(`Error generating ${period} summary:`, err);
      let errorMsg = err instanceof Error ? err.message : 'Failed to generate summary';
      // A billing 403 carries a structured `upgradeRequired` flag, but its
      // message ("Usage limit reached") may not contain the phrase the inline
      // CTA keys on ("upgrade your plan"). Without this, a free user who hits
      // the limit gets a red dead-end with no way to pay. Normalize so the
      // "View plans" CTA reliably appears — but only when the flag is set, so
      // transient "rate limit" infra errors still don't nudge an upgrade.
      if ((err as any)?.upgradeRequired && !/upgrade your plan/i.test(errorMsg)) {
        errorMsg = `${errorMsg.replace(/[.\s]*$/, '')}. Please upgrade your plan to continue generating summaries.`;
      }
      if (period === 'comments') {
        setCommentPeriodState(prev => ({
          ...prev,
          isGenerating: false,
          generationProgress: 0,
          generationStartedAt: null,
          error: errorMsg,
        }));
      } else {
        setReplyCommentPeriodState(prev => ({
          ...prev,
          isGenerating: false,
          generationProgress: 0,
          generationStartedAt: null,
          error: errorMsg,
        }));
      }
    }
  }, [docketNumber, filingsByPeriod, loadExistingSummaries, nprmFiles.length, pnFiles.length, prevailingInstrument]);

  // Auto-resume Generate after the user signs in via the auth wall.
  // The page that triggered the wall is the same one we're rendering
  // now (we redirected back to it), so we just pick up the intent and
  // re-fire with the period-specific state.
  // Re-fire a summary generation that the sign-in wall interrupted, restoring
  // the user's ACTUAL configuration from the stashed intent payload rather than
  // the post-reload default state (magic-link sign-in fully reloads the page and
  // wipes React state — without this, the resumed summary silently ignored the
  // topics/client-angle/detail the user picked). Single-consume, so it's safe to
  // call from both the event and a mount-time replay.
  const resumePendingSummary = useCallback(() => {
    // Only consume OUR intent — otherwise a pending follow_docket intent
    // (from the same page's FollowButton) gets swallowed and discarded here.
    const pending = consumePendingAuthIntent(
      (i) => !!i.intentKey && i.intentKey.startsWith('generate_summary:')
    );
    if (!pending?.intentKey) return;
    const m = /^generate_summary:([^:]+):(comments|reply_comments)$/.exec(pending.intentKey);
    if (!m) return;
    const [, intentDocket, intentPeriod] = m;
    if (intentDocket !== docketNumber) return;
    const period = intentPeriod as 'comments' | 'reply_comments';
    // Rehydrate the config, converting the Set fields back from the arrays we
    // stashed (Sets don't survive JSON.stringify).
    const raw = (pending.payload as Record<string, unknown>) || {};
    const toSet = (v: unknown) => (Array.isArray(v) ? new Set(v as string[]) : undefined);
    const cfg: Partial<PeriodSummaryState> = {
      ...(raw as Partial<PeriodSummaryState>),
      ...(raw.selectedFilingIds !== undefined ? { selectedFilingIds: toSet(raw.selectedFilingIds) } : {}),
      ...(raw.selectedOutOfWindowIds !== undefined ? { selectedOutOfWindowIds: toSet(raw.selectedOutOfWindowIds) } : {}),
      ...(raw.selectedMisclassifiedIds !== undefined ? { selectedMisclassifiedIds: toSet(raw.selectedMisclassifiedIds) } : {}),
    };
    const setState = period === 'comments' ? setCommentPeriodState : setReplyCommentPeriodState;
    const base = period === 'comments' ? commentPeriodState : replyCommentPeriodState;
    // Reflect the restored config in the form, and generate from it directly
    // (setState is async, so pass the merged config explicitly).
    setState(prev => ({ ...prev, ...cfg }));
    generatePeriodSummary(period, { ...base, ...cfg });
  }, [docketNumber, commentPeriodState, replyCommentPeriodState, generatePeriodSummary]);

  // Fire on the post-sign-in event.
  useEffect(() => {
    const onResume = () => resumePendingSummary();
    window.addEventListener('cg:auth-resume', onResume);
    return () => window.removeEventListener('cg:auth-resume', onResume);
  }, [resumePendingSummary]);

  // Replay once on mount too: after a magic-link reload the auth-resume event
  // often fires before this section mounts (it's gated behind the page's
  // metadata load), so the event alone is unreliable. Wait until filings are
  // hydrated (generatePeriodSummary needs them), then attempt a single replay.
  const summaryReplayedRef = useRef(false);
  useEffect(() => {
    if (summaryReplayedRef.current) return;
    if (filingsHydratedForDocket !== docketNumber) return;
    summaryReplayedRef.current = true;
    resumePendingSummary();
  }, [filingsHydratedForDocket, docketNumber, resumePendingSummary]);

  // Initialize data
  useEffect(() => {
    if (docketNumber) {
      setFilingsHydratedForDocket(null);
      fetchTimelineData();
    }
  }, [docketNumber, fetchTimelineData]);

  // Rescue re-fetch: fires when summariesActiveTimeline becomes available (e.g. parent
  // provides syntheticTimelineForFilings for PN dockets) but the initial fetch (from
  // fetchTimelineData) produced no classified filings because the timeline wasn't ready.
  // For NPRM dockets, fetchTimelineData already calls fetchAllFilings directly with the
  // DB timeline so this effect is effectively a no-op (filingsByPeriod will be non-empty).
  useEffect(() => {
    if (!docketNumber) return;
    if (!summariesActiveTimeline) return; // no timeline to classify with
    if (filingsHydratedForDocket !== docketNumber) return; // wait for initial fetch
    if (filingsByPeriod.comments.length > 0 || filingsByPeriod.replyComments.length > 0) return; // already classified
    fetchAllFilings(summariesActiveTimeline);
  }, [docketNumber, summariesActiveTimeline, filingsHydratedForDocket, fetchAllFilings, filingsByPeriod.comments.length, filingsByPeriod.replyComments.length]);

  // Defer non-critical loads until filings are ready for first paint.
  useEffect(() => {
    if (!docketNumber) return;
    if (filingsHydratedForDocket !== docketNumber) return;
    if (prevailingInstrument === 'public_notice') {
      loadPnFiles();
    } else {
      loadNprmFiles();
    }
    loadExistingSummaries();
  }, [
    docketNumber,
    filingsHydratedForDocket,
    loadNprmFiles,
    loadPnFiles,
    loadExistingSummaries,
    prevailingInstrument,
  ]);

  // Poll for completion only while a summary generation job is actually
  // in flight. Previously this ran every 30s indefinitely for every open
  // proceedings page, even when no job was running — pure DB tax.
  const hasInFlightJob = Boolean(
    commentPeriodState.jobId || replyCommentPeriodState.jobId
  );
  useEffect(() => {
    if (!docketNumber || !hasInFlightJob) return;

    const interval = setInterval(() => {
      loadExistingSummaries();
    }, 30000);

    return () => clearInterval(interval);
  }, [docketNumber, loadExistingSummaries, hasInFlightJob]);

  // Helper functions for filing display
  const getFilerName = (filing: Filing) => {
    if (filing.filers && filing.filers.length > 0) {
      return filing.filers[0].name;
    }
    return 'Unknown Filer';
  };

  const getFilingType = (filing: Filing) => {
    return filing.submissiontype?.short || filing.filing_type || 'UNKNOWN';
  };

  // Render period summary section
  const renderPeriodSection = (
    period: 'comments' | 'reply_comments',
    state: PeriodSummaryState,
    setState: React.Dispatch<React.SetStateAction<PeriodSummaryState>>,
    filings: Filing[],
    otherFilings: Filing[],
    misclassifiedFilings: Filing[]
  ) => {
    const periodName = period === 'comments' ? 'Comment Period' : 'Reply Comment Period';
    const periodCount = filings.length;

    // Handle selection mode change
    const handleSelectionModeChange = (mode: 'all' | 'specific') => {
      if (mode === 'all') {
        setState(prev => ({ ...prev, selectionMode: 'all', selectedFilingIds: new Set() }));
      } else {
        // When switching to specific, initialize with all filings selected
        const allIds = new Set(filings.map(f => f.id_submission));
        setState(prev => ({ ...prev, selectionMode: 'specific', selectedFilingIds: allIds }));
      }
    };

    // Handle filing toggle
    const handleFilingToggle = (submissionId: string) => {
      setState(prev => {
        const newSelected = new Set(prev.selectedFilingIds);
        if (newSelected.has(submissionId)) {
          newSelected.delete(submissionId);
        } else {
          newSelected.add(submissionId);
        }
        return { ...prev, selectedFilingIds: newSelected };
      });
    };

    // Handle select all
    const handleSelectAll = () => {
      const allIds = new Set(filings.map(f => f.id_submission));
      setState(prev => ({ ...prev, selectedFilingIds: allIds }));
    };

    // Handle deselect all
    const handleDeselectAll = () => {
      setState(prev => ({ ...prev, selectedFilingIds: new Set() }));
    };

    const periodColors = {
      bg: 'bg-emerald-50/90',
      border: 'border-emerald-200',
      text: 'text-emerald-800',
      badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
      button: 'bg-gray-900 hover:bg-emerald-600',
      cardBg: 'bg-white',
      cardBorder: 'border-stone-200',
    };

    const getDetailLevelLabel = (level?: string) => {
      switch(level) {
        case 'concise': return 'Concise';
        case 'standard': return 'Standard';
        case 'detailed': return 'Detailed';
        default: return 'Standard';
      }
    };

    const getConfigurationValidationError = (): string | null => {
      if (state.selectionMode === 'specific' && state.selectedFilingIds.size === 0) {
        return 'Select at least one filing when using specific selection.';
      }
      // NPRM / Public-Notice doc selection no longer blocks generation — we
      // auto-default to the most recent revision (quick-summary path); the
      // picker stays available for users who want a different one.
      return null;
    };

    const configurationValidationError = getConfigurationValidationError();

    return (
      <div className="mb-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h3 className="text-2xl font-bold text-gray-900">{periodName} Summary</h3>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>
              {periodCount} {periodCount === 1 ? 'filing' : 'filings'}
            </span>
            <span aria-hidden className="text-gray-300">·</span>
            <button
              type="button"
              onClick={() => fetchAllFilings(summariesActiveTimeline)}
              disabled={loading}
              className="text-xs text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline disabled:opacity-50"
              title="Run full-range mismatch check and refresh filings"
            >
              Refresh
            </button>
          </div>
        </div>

        {state.summary && (
          <Link
            to={state.summary.share_token ? `/s/${state.summary.share_token}` : `/summaries/${docketNumber}`}
            className={`group mb-6 flex items-center justify-between gap-4 rounded-xl border-2 ${periodColors.border} ${periodColors.bg} p-4 shadow-sm transition-shadow hover:shadow-md`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
              <div className="min-w-0">
                <div className={`text-base font-semibold ${periodColors.text}`}>
                  Summary ready
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                  {state.summary.topics?.slice(0, 3).map((topic, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {topic}
                    </span>
                  ))}
                  {state.summary.topics && state.summary.topics.length > 3 && (
                    <span>+{state.summary.topics.length - 3} more</span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <FileCheck className="h-3 w-3" />
                    {state.summary.document_count} {state.summary.document_count === 1 ? 'document' : 'documents'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(state.summary.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-gray-700 group-hover:text-gray-900">
              View summary
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        )}

        {state.isGenerating ? (
          <div className="mt-6" ref={topicsStepRef}>
            <GenerationProgress
              progress={state.generationProgress}
              startedAt={state.generationStartedAt}
              filingNoun={period === 'comments' ? 'comment' : 'reply comment'}
              filingCount={periodCount}
              topics={state.topics}
            />
          </div>
        ) : (
        <div
          ref={topicsStepRef}
          className={
            state.summary
              ? 'mt-6 border-t border-cg-border/60 pt-6'
              : 'mt-4'
          }
        >
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cg-green/10 text-cg-green">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-cg-ink">
                {state.summary ? 'Generate a new version' : 'Generate a personalized summary'}
              </h4>
              <p className="mt-0.5 text-sm text-neutral-600">
                Page-cited, topic-tagged, and ready to send. Typical run takes about three minutes.
              </p>
            </div>
          </div>

          <div className="mt-5">
            <GenerateSummaryForm
              topics={state.topics}
              topicsInputMode={state.topicsInputMode}
              onTopicsChange={(topics) => setState(prev => ({ ...prev, topics }))}
              onInputModeChange={(mode) => setState(prev => ({ ...prev, topicsInputMode: mode }))}
              topicsFirstFieldRef={topicsFirstFieldRef}

              selectionMode={state.selectionMode}
              selectedFilingIds={state.selectedFilingIds}
              filings={filings}
              commentsCloseOn={
                activePeriodTab === 'reply_comments'
                  ? summariesActiveTimeline?.reply_comments_period_end ?? null
                  : summariesActiveTimeline?.comments_period_end ?? null
              }
              onSelectionModeChange={handleSelectionModeChange}
              onFilingToggle={handleFilingToggle}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}

              outOfWindowFilings={otherFilings}
              selectedOutOfWindowIds={state.selectedOutOfWindowIds}
              onOutOfWindowToggle={(id) =>
                setState(prev => {
                  const next = new Set(prev.selectedOutOfWindowIds);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return { ...prev, selectedOutOfWindowIds: next };
                })
              }
              onOutOfWindowSelectAll={() =>
                setState(prev => ({
                  ...prev,
                  selectedOutOfWindowIds: new Set(
                    otherFilings.map(f => f.id_submission)
                  ),
                }))
              }
              onOutOfWindowDeselectAll={() =>
                setState(prev => ({
                  ...prev,
                  selectedOutOfWindowIds: new Set(),
                }))
              }

              misclassifiedFilings={misclassifiedFilings}
              selectedMisclassifiedIds={state.selectedMisclassifiedIds}
              onMisclassifiedToggle={(id) =>
                setState(prev => {
                  const next = new Set(prev.selectedMisclassifiedIds);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return { ...prev, selectedMisclassifiedIds: next };
                })
              }
              onMisclassifiedSelectAll={() =>
                setState(prev => ({
                  ...prev,
                  selectedMisclassifiedIds: new Set(
                    misclassifiedFilings.map(f => f.id_submission)
                  ),
                }))
              }
              onMisclassifiedDeselectAll={() =>
                setState(prev => ({
                  ...prev,
                  selectedMisclassifiedIds: new Set(),
                }))
              }

              detailLevel={state.detailLevel}
              onDetailLevelChange={(level) => setState(prev => ({ ...prev, detailLevel: level }))}

              docketNumber={docketNumber}
              selectedStakeholders={state.selectedStakeholders}
              onStakeholdersChange={(stakeholders) =>
                setState(prev => ({ ...prev, selectedStakeholders: stakeholders }))
              }

              showNprmPicker={showNprmPicker}
              nprmFiles={nprmFiles}
              selectedNprmFile={state.selectedNprmFile}
              onNprmFileChange={(file) => setState(prev => ({ ...prev, selectedNprmFile: file }))}
              isLoadingNprmFiles={isLoadingNprmFiles}
              isFetchingNprmFiles={isFetchingNprmFiles}
              fetchingNprmError={fetchingNprmError}
              onFetchNprmFiles={fetchAndUploadNprmFiles}
              nprmCount={nprmCount}

              showPnPicker={showPnPicker}
              publicNoticeFiles={pnFiles}
              selectedPublicNoticeFile={state.selectedPnFile}
              onPublicNoticeFileChange={(file) => setState(prev => ({ ...prev, selectedPnFile: file }))}
              isLoadingPnFiles={isLoadingPnFiles}
              isFetchingPnFiles={isFetchingPnFiles}
              fetchingPnError={fetchingPnError}
              onFetchPnFiles={fetchAndUploadPnFile}
              publicNoticeCount={publicNoticeCount}

              getFilerName={getFilerName}
              getFilingType={getFilingType}
              getFilingTypeTag={getFilingTypeTag}
              formatDateForDisplay={formatDateForDisplay}

              executiveSummaryFocus={state.executiveSummaryFocus}
              onExecutiveSummaryFocusChange={(focus) =>
                setState(prev => ({ ...prev, executiveSummaryFocus: focus }))
              }

              forceRefresh={state.forceRefresh}
              onForceRefreshChange={(refresh) => setState(prev => ({ ...prev, forceRefresh: refresh }))}
              onGenerate={() =>
                requireAuth(
                  () => generatePeriodSummary(period, state),
                  {
                    intentLabel: 'generate this summary',
                    intentKey: `generate_summary:${docketNumber}:${period}`,
                    // Preserve the user's actual configuration across the
                    // magic-link sign-in reload so the resumed summary matches
                    // what they picked (not post-reload defaults). `topics` is
                    // required — without it the resumed run fails validation.
                    // Sets aren't JSON-serializable, so store them as arrays and
                    // rehydrate in resumePendingSummary.
                    intentPayload: {
                      topics: state.topics,
                      topicsInputMode: state.topicsInputMode,
                      selectionMode: state.selectionMode,
                      selectedFilingIds: Array.from(state.selectedFilingIds),
                      selectedOutOfWindowIds: Array.from(state.selectedOutOfWindowIds),
                      selectedMisclassifiedIds: Array.from(state.selectedMisclassifiedIds),
                      detailLevel: state.detailLevel,
                      selectedStakeholders: state.selectedStakeholders,
                      executiveSummaryFocus: state.executiveSummaryFocus,
                      selectedNprmFile: state.selectedNprmFile,
                      selectedPnFile: state.selectedPnFile,
                      forceRefresh: state.forceRefresh,
                    },
                  }
                )
              }
              isGenerating={state.isGenerating}
              error={state.error}
              jobId={state.jobId}
              periodCount={periodCount}
              periodName={periodName}
              periodColors={periodColors}
              validationError={configurationValidationError}
            />
          </div>
        </div>
        )}
      </div>
    );
  };

  const currentPeriodState = activePeriodTab === 'comments' ? commentPeriodState : replyCommentPeriodState;
  const setCurrentPeriodState = activePeriodTab === 'comments' ? setCommentPeriodState : setReplyCommentPeriodState;
  const currentPeriodFilings = activePeriodTab === 'comments' ? filingsByPeriod.comments : filingsByPeriod.replyComments;

  return (
    <div className="mb-8">
      {prevailingInstrument === 'public_notice' && (
        <p className="mb-4 rounded-lg border border-amber-100 bg-amber-50/90 px-3 py-2 text-sm text-amber-950">
          This docket is anchored on a public notice (not an NPRM). Summaries use filings in the
          comment and reply windows below.
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-6 h-6 text-green-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading filings...</span>
        </div>
      ) : error ? (
        <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Tab Navigation — only render when there's more than one period to switch between. */}
          {availableTabs.length > 1 && (
            <div className="mb-4">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {availableTabs.map((tab) => {
                    const isActive = activePeriodTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActivePeriodTab(tab.id)}
                        className={`flex items-center space-x-2 border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? 'border-emerald-600 text-emerald-800'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        <span>
                          {tab.label} ({tab.count})
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          )}

          {/* Active Tab Content */}
          {availableTabs.length === 0 ? (
            // Distinguish "filings exist but not yet classified" from a true
            // empty state. Classification needs `summariesActiveTimeline`
            // (cycle-start + comment/reply deadlines); if filings have been
            // hydrated but the timeline is still loading, show a transient
            // loading state instead of the misleading "no filings" message.
            filings.length > 0 && !summariesActiveTimeline ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-6 h-6 text-green-600 animate-spin" />
                <span className="ml-3 text-gray-600">Loading filing periods...</span>
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  No filings found in Comment or Reply Comment periods.
                </p>
                <p className="text-sm text-gray-500">
                  Filings will appear here once they are categorized by period.
                </p>
              </div>
            )
          ) : (
            renderPeriodSection(
              activePeriodTab,
              currentPeriodState,
              setCurrentPeriodState,
              currentPeriodFilings,
              filingsByPeriod.others,
              filingsByPeriod.misclassified
            )
          )}
        </>
      )}
    </div>
  );
};
